/**
 * lp-015. What is wrong with our own draft, before it goes out.
 *
 * The counterparty review answers "what did they ask for, and do we take it".
 * This answers a question nobody was asking on their behalf: does the document
 * WE are about to send agree with itself, and with the policy we wrote down.
 *
 * The playbook family is first because it is nearly free — `locateOnLadder`,
 * the concept vocabulary, the rungs and the escalation triggers were all built
 * for lp-012 and none of them care whose text they are reading. It is also the
 * family that makes a playbook pay off before any counterparty is involved: a
 * drafter who sets holdover at 110% because a broker asked has already crossed
 * their own floor, and until now nothing said so.
 */
import { locateOnLadder, ladderStatement } from './counterparty_ladder.js';

export const CHECK_FAMILIES = Object.freeze({
  PLAYBOOK: 'playbook',
  CHECKLIST: 'checklist',
});

export const SEVERITIES = Object.freeze(['high', 'medium', 'low']);

/**
 * A finding's identity, and why it is not a counter.
 *
 * These are re-run constantly — on save, on structure change — and a drafter
 * who sets one aside must not see it again on the next keystroke. So identity
 * has to survive a re-run, which rules out an array index or a fresh id per
 * pass.
 *
 * It also has to CHANGE when the thing found changes. A floor breach at 110%
 * that somebody set aside is a decision about 110%; if the document later says
 * 115% that is a new decision, and resurrecting the finding is correct rather
 * than annoying. So the evidence is part of the identity, not just the place.
 */
export function findingId(check, scope, evidence) {
  return [check, scope, String(evidence == null ? '' : evidence)].join(':');
}

/**
 * A position the document takes, measured against the playbook's own ladder.
 *
 * Only breaches are reported. A document sitting at or better than the declared
 * position is the ordinary case and saying so on every concept would bury the
 * three that matter — the panel is for what needs attention, and "nothing is
 * wrong here" is what the absence of a finding already means.
 */
const PLACEMENT_SEVERITY = Object.freeze({
  past_hard_limit: 'high',
  below_fallback: 'medium',
});

export function playbookFindings(documentValues, contexts, concepts) {
  const labels = new Map(
    (concepts || []).map(concept => [concept.id, concept.label || concept.id]),
  );

  return (documentValues || [])
    .map(reading => {
      const context = (contexts || {})[reading.concept];
      // No declared position is not a finding. A concept the customer
      // deliberately declined, or has not reached yet, has no floor to cross —
      // and reporting one would be advising off a policy nobody stated.
      if (!context || !context.hasDeclaredStance) return null;

      const location = locateOnLadder(reading.value, context.ladder);
      if (!location) return null;

      const severity = PLACEMENT_SEVERITY[location.placement];
      if (!severity) return null;

      const label = labels.get(reading.concept) || reading.concept;
      return {
        id: findingId('playbook_ladder', reading.concept, reading.value),
        family: CHECK_FAMILIES.PLAYBOOK,
        check: 'playbook_ladder',
        severity,
        concept: reading.concept,
        summary: `${label} at ${reading.value} ${location.metric} is past your ${
          location.placement === 'past_hard_limit' ? 'minimum' : 'fallback'
        } of ${location.boundaryValue}`,
        statement: ladderStatement(location, reading.value),
        quote: reading.quote || null,
        at: reading.section || null,
        // lp-016 §7. The figure, not the sentence it sits in.
        //
        // The mechanical families paint the exact characters they objected to;
        // this one used to mark the whole clause, which on a holdover paragraph
        // is four lines of prose with one number in it — and the number is the
        // entire finding.
        hits: figureHits(reading),
        // The customer's own approved wording, which is the only reason a
        // finding is allowed to propose anything at all. Every other family
        // reports and stops.
        //
        // Said in the panel as a sentence rather than offered as a button: the
        // panel cannot edit the lease, and a control labelled "Use 150%" that
        // changes nothing is worse than no control. The statement names the
        // boundary that was crossed; this names the rung to move to, which is
        // a different number and the one a drafter actually needs.
        proposal: proposalFor(context, location),
        metric: location.metric || null,
        escalation: context.escalation || null,
      };
    })
    .filter(Boolean);
}

/**
 * The characters that carry the figure we read.
 *
 * Every numeric token in the quote is parsed and compared to the value the
 * reader returned, rather than the value being formatted and searched for:
 * "1,100" and "1100" and "$1,100.00" are the same figure written three ways,
 * and a document uses whichever it likes. The token is taken with its currency
 * sign or percent attached, because "110" alone underlined inside "110%" reads
 * as a mistake in the highlight rather than as precision.
 *
 * Falls back to the whole quote when the figure is spelled in words, or when
 * the reader derived it rather than copying it. That is the old behaviour, and
 * it is still better than marking nothing.
 */
const NUMERIC_TOKEN = /\$?\s?\d[\d,]*(?:\.\d+)?\s?%?/g;

function figureHits(reading) {
  const pid = reading.section && reading.section.pid;
  const quote = reading.quote;
  if (!pid || !quote) return [];

  const figure = figureIn(quote, reading.value);
  return [{ pid: pid, text: figure || quote }];
}

function figureIn(quote, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;

  const matches = String(quote).match(NUMERIC_TOKEN) || [];
  const hit = matches.find(token => {
    const bare = token.replace(/[^0-9.]/g, '');
    return bare !== '' && Number(bare) === value;
  });

  return hit ? hit.trim() : null;
}

/**
 * What to offer instead, in the customer's own words.
 *
 * The rung one step better than the one that was crossed: a document past the
 * hard limit is offered the fallback, and one past the fallback is offered the
 * declared position. Offering the floor to somebody who just crossed it would
 * be advising them to hold the least they said they would accept.
 */
function proposalFor(context, location) {
  const ladder = context.ladder || {};
  if (location.placement === 'past_hard_limit') {
    return numberOrNull(ladder.fallback) ?? numberOrNull(ladder.stance);
  }
  return numberOrNull(ladder.stance);
}

function numberOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Ordered for a checklist, not for a report.
 *
 * Severity first because that is how the panel groups, then document position,
 * so walking the list walks the lease rather than jumping around it.
 */
export function orderFindings(findings) {
  const rank = new Map(SEVERITIES.map((s, i) => [s, i]));
  return (findings || []).slice().sort((a, b) => {
    const bySeverity = rank.get(a.severity) - rank.get(b.severity);
    if (bySeverity !== 0) return bySeverity;
    return (a.at?.order ?? Infinity) - (b.at?.order ?? Infinity);
  });
}


/**
 * lp-017. The customer's own items, as findings.
 *
 * Thinner than `playbookFindings` because the judgement already happened: the
 * reader only speaks where a clause fails an item, so there is no ladder to
 * locate a value on and nothing left to decide here. What this does is give the
 * finding the shape every other family has — an identity that survives a re-run,
 * a place, and something to highlight.
 *
 * Severity comes from the item rather than from us. The customer decided how
 * serious it was when they wrote it, and they are the only ones who could.
 */
export function checklistFindings(raw) {
  return (raw || []).map(finding => ({
    id: findingId(
      `checklist_${finding.item}`,
      finding.blockPid || 'document',
      finding.statement,
    ),
    family: CHECK_FAMILIES.CHECKLIST,
    check: 'checklist_item',
    severity: SEVERITIES.includes(finding.severity) ? finding.severity : 'medium',
    // The item as they wrote it, shown beside what it found. A drafter reading
    // an unexpected finding needs to see the question that produced it before
    // they can decide whether the answer is right.
    itemText: finding.itemText || null,
    summary: finding.statement,
    statement: finding.statement,
    quote: finding.quote || null,
    at: finding.blockPid
      ? { pid: finding.blockPid, number: finding.sectionNumber || null, order: null }
      : null,
    hits: finding.quote && finding.blockPid
      ? [{ pid: finding.blockPid, text: finding.quote }]
      : [],
    proposal: null,
  }));
}


/**
 * lp-016 §3, Family F. The same fact stated twice, differently.
 *
 * Both clauses are carried, and both are highlighted. A contradiction with one
 * side shown is an assertion; with both sides shown it is something a drafter
 * settles in ten seconds without trusting us at all — which is the entire
 * reason this family is worth a model call.
 *
 * High, always. The catalogue calls this the most valuable family there is
 * because the summary page and the body are edited by different people at
 * different times, and a lease that disagrees with itself about the term is
 * wrong in a way nobody can read past.
 */
export function contradictionFindings(raw) {
  return (raw || []).map(finding => ({
    id: findingId(
      'contradiction',
      `${finding.blockPid}:${finding.otherBlockPid}`,
      finding.fact || finding.statement,
    ),
    family: 'integrity',
    check: 'contradiction',
    severity: 'high',
    summary: `Two clauses disagree about ${finding.fact}`,
    statement: finding.statement,
    quote: finding.quote || null,
    // The second clause, named as such. It is not more evidence for the first
    // claim; it is the other half of the claim.
    otherQuote: finding.otherQuote || null,
    otherAt: finding.otherBlockPid
      ? { pid: finding.otherBlockPid, number: finding.otherSectionNumber || null }
      : null,
    at: finding.blockPid
      ? { pid: finding.blockPid, number: finding.sectionNumber || null, order: null }
      : null,
    hits: [
      finding.blockPid && finding.quote
        ? { pid: finding.blockPid, text: finding.quote }
        : null,
      finding.otherBlockPid && finding.otherQuote
        ? { pid: finding.otherBlockPid, text: finding.otherQuote }
        : null,
    ].filter(Boolean),
    proposal: null,
  }));
}

/**
 * lp-016 §4. The pass that reads.
 *
 * Four kinds, each named in the panel in the drafter's terms rather than ours.
 * A finding that says "semantic issue" tells somebody nothing about whether to
 * open it; "a sentence that stops before it finishes" tells them exactly.
 */
const SEMANTIC_WHAT = {
  truncated: 'A sentence that does not finish',
  mismatch: 'A heading and a body about different things',
  misreference: 'A reference pointing somewhere else',
  foreign: 'Text from another transaction',
};

export function semanticFindings(raw) {
  return (raw || []).map(finding => ({
    id: findingId(
      `semantic_${finding.kind}`,
      finding.blockPid || 'document',
      finding.quote,
    ),
    family: 'integrity',
    check: `semantic_${finding.kind}`,
    severity: SEVERITIES.includes(finding.severity) ? finding.severity : 'medium',
    summary: SEMANTIC_WHAT[finding.kind] || 'Something that does not read right',
    statement: finding.statement,
    quote: finding.quote || null,
    at: finding.blockPid
      ? { pid: finding.blockPid, number: finding.sectionNumber || null, order: null }
      : null,
    hits:
      finding.blockPid && finding.quote
        ? [{ pid: finding.blockPid, text: finding.quote }]
        : [],
    proposal: null,
  }));
}
